// Connection to Supabase (sign-in + database functions) and the Microsoft Graph token for admin sync.
// Uses Supabase's sign-in and database clients directly: the app needs nothing else from supabase-js.
import { AuthClient } from '@supabase/auth-js';
import { PostgrestClient } from '@supabase/postgrest-js';
import { GOOGLE_RETURN_PATH, googleAuthUrl, googleStartOrigin, parseGoogleReturn, randomString, safeReturnPath, sha256Hex } from './google.js';

// Filled in by build.mjs from the hosting settings (VV_SUPABASE_URL, VV_SUPABASE_KEY, VV_GOOGLE_CLIENT_ID).
// eslint-disable-next-line no-undef
const cfg = typeof __VV_CONFIG__ !== 'undefined' ? __VV_CONFIG__ : {};
export const config = {
  supabaseUrl: cfg.supabaseUrl || '',
  supabaseAnonKey: cfg.supabaseAnonKey || '',
  whatsapp: cfg.whatsapp || '918484969689',
  version: cfg.version || 'dev',
  // Parents' Google sign-in on the app's own address (google.js); without a client ID, through Supabase.
  googleClientId: cfg.googleClientId || '',
  appOrigin: cfg.appOrigin || '',
};

export const configured = Boolean(config.supabaseUrl && config.supabaseAnonKey);

const STORAGE_KEY = 'vv-lms-auth';
const GRAPH_KEY = 'vv-graph';
const GRAPH_WANTED = 'vv-graph-wanted';
const BASIC_SCOPES = 'openid profile email';
export const GRAPH_SCOPES = `${BASIC_SCOPES} Files.Read.All`;
// For keeping the batches' Microsoft groups in step: find groups and change their members, look people up
// by email.
export const GROUP_SCOPES = `${BASIC_SCOPES} Files.Read.All GroupMember.Read.All GroupMember.ReadWrite.All User.ReadBasic.All`;

// Microsoft's own token comes back with every sign-in. The app needs it only right after an admin allows
// OneDrive access (for Sync now) or group changes, so it is kept for that tab alone, for 50 minutes, for the
// person who allowed it, and never saved with the rest of the sign-in on the device.
function keepGraphToken(token, userId) {
  try {
    const raw = sessionStorage.getItem(GRAPH_WANTED);
    if (!raw || !token || !userId) return;
    let asked = null;
    try { asked = JSON.parse(raw); } catch (e) { asked = null; }
    if (typeof asked === 'number') asked = { at: asked, purpose: 'files' };
    if (!asked || !asked.at) return;
    sessionStorage.removeItem(GRAPH_WANTED);
    if (Date.now() - asked.at < 15 * 60 * 1000) {
      const purpose = asked.purpose === 'groups' ? 'groups' : 'files';
      sessionStorage.setItem(GRAPH_KEY, JSON.stringify({ token, purpose, user: userId, expires: Date.now() + 50 * 60 * 1000 }));
    }
  } catch (e) { /* private mode: the admin will be asked again */ }
}

// Who is signed in on this device, from the saved sign-in (null if nobody).
function signedInUserId() {
  try {
    const s = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    return (s && s.user && s.user.id) || null;
  } catch (e) { return null; }
}

const storage = {
  getItem(key) {
    try { return localStorage.getItem(key); } catch (e) { return null; }
  },
  setItem(key, value) {
    let text = value;
    if (key === STORAGE_KEY) {
      try {
        const session = JSON.parse(value);
        if (session && (session.provider_token || session.provider_refresh_token)) {
          keepGraphToken(session.provider_token, session.user && session.user.id);
          delete session.provider_token;
          delete session.provider_refresh_token;
          text = JSON.stringify(session);
        }
      } catch (e) { /* not JSON: store as it is */ }
    }
    try { localStorage.setItem(key, text); } catch (e) { /* storage full or blocked */ }
  },
  removeItem(key) {
    try { localStorage.removeItem(key); } catch (e) { /* ignore */ }
  },
};

const auth = configured
  ? new AuthClient({
      url: `${config.supabaseUrl}/auth/v1`,
      headers: { Authorization: `Bearer ${config.supabaseAnonKey}`, apikey: config.supabaseAnonKey },
      storageKey: STORAGE_KEY,
      storage,
      flowType: 'pkce',
      // Supabase's own sign-in returns (?code=... from Microsoft, or an error) are the client's to read; a
      // parent's Google sign-in coming back to /auth/google is the app's (finishGoogleSignIn).
      detectSessionInUrl: (url, params) => url.pathname !== GOOGLE_RETURN_PATH
        && Boolean(params.access_token || params.error || params.error_description || params.error_code),
      persistSession: true,
      autoRefreshToken: true,
    })
  : null;

// Every database call carries the signed-in person's token (or the public key before sign-in).
async function authedFetch(input, init = {}) {
  const { data } = await auth.getSession();
  const headers = new Headers(init.headers);
  if (!headers.has('apikey')) headers.set('apikey', config.supabaseAnonKey);
  headers.set('Authorization', `Bearer ${(data.session && data.session.access_token) || config.supabaseAnonKey}`);
  return fetch(input, { ...init, headers });
}

const db = configured ? new PostgrestClient(`${config.supabaseUrl}/rest/v1`, { fetch: authedFetch, timeout: 30000 }) : null;


// An error the screens can show as-is, with a short machine-readable reason.
export class AppError extends Error {
  constructor(message, reason, status) {
    super(message);
    this.reason = reason || 'error';
    this.status = status || 0;
  }
}

function friendly(error) {
  if (!error) return new AppError('Something went wrong. Please try again.');
  const hint = error.hint || '';
  const msg = error.message || '';
  if (hint === 'signed_out' || /JWT|jwt|not authenticated/.test(msg)) return new AppError('Please sign in again.', 'signed_out', 401);
  if (['not_registered', 'blocked', 'forbidden', 'no_access', 'not_found', 'wrong_sign_in'].includes(hint)) return new AppError(msg, hint);
  if (/AbortError|aborted/.test(msg)) return new AppError('The connection is very slow right now. Check your internet and try again.', 'offline');
  if (/Failed to fetch|NetworkError|Load failed|fetch/.test(msg)) return new AppError('You seem to be offline. Check your connection and try again.', 'offline');
  if (error.code === 'PGRST202') return new AppError('The app needs an update on the server. Please tell the academy.', 'server');
  if (error.code === '22P02') return new AppError('That page doesn’t exist. The link may be old or cut short.', 'not_found');
  return new AppError(msg || 'Something went wrong. Please try again.', hint || 'error');
}

export async function rpc(name, args) {
  if (!db) throw new AppError('The app is not connected yet.', 'not_configured');
  const { data, error } = await db.rpc(name, args || {});
  if (error) throw friendly(error);
  return data;
}

export async function getSession() {
  if (!auth) return null;
  const { data } = await auth.getSession();
  return data.session || null;
}

export function onAuthChange(fn) {
  if (!auth) return () => {};
  // (Microsoft's token is kept only where this tab saves its own sign-in, in storage.setItem: a sign-in
  // finished in another tab never counts as this tab's permission.)
  const { data } = auth.onAuthStateChange((event, session) => {
    if (event === 'SIGNED_OUT') forgetGraphToken();
    fn(event, session);
  });
  return () => data.subscription.unsubscribe();
}

// Which sign-in was started (Microsoft for students and staff, Google for parents), for the error wording.
const SIGNIN_PROVIDER = 'vv-signin-provider';

// Where to come back to after signing in: a page of this app only.
function backTo(path) {
  const safe = typeof path === 'string' && /^\/(?![\/\\])/.test(path) ? path : '/';
  const url = new URL(safe, location.origin);
  return url.origin === location.origin ? url.href : `${location.origin}/`;
}

export async function signIn(returnTo, provider = 'azure') {
  if (!auth) throw new AppError('The app is not connected yet.', 'not_configured');
  const redirectTo = backTo(returnTo);
  try {
    sessionStorage.setItem(SIGNIN_PROVIDER, provider);
    // An ordinary sign-in: whatever Microsoft returns isn't kept for OneDrive or group changes.
    sessionStorage.removeItem(GRAPH_WANTED);
    sessionStorage.removeItem(GRAPH_KEY);
  } catch (e) { /* ignore */ }
  if (provider === 'google' && config.googleClientId) {
    await startGoogleSignIn(new URL(redirectTo).pathname + new URL(redirectTo).search);
    return;
  }
  const { error } = provider === 'google'
    ? await auth.signInWithOAuth({ provider: 'google', options: { redirectTo, queryParams: { prompt: 'select_account' } } })
    : await auth.signInWithOAuth({ provider: 'azure', options: { scopes: BASIC_SCOPES, redirectTo, queryParams: { prompt: 'select_account' } } });
  if (error) throw friendly(error);
}

/* ---------- Parents: Google sign-in on the app's own address (see google.js) ---------- */
// The sign-in waiting for Google's answer, under its state: in localStorage, so the answer still counts if
// it opens in another tab of the same browser (or in the installed app, which shares Chrome's storage).
const GOOGLE_PENDING = 'vv-google-signin:';
const GOOGLE_MAX_AGE = 15 * 60 * 1000;

function googleProblem(ref = '') {
  return `Google sign-in didn’t finish. Google sign-in is for parents the academy has added; students and teachers sign in with Microsoft. If you’re a parent and it keeps happening, message the academy${ref}.`;
}

function dropOldGoogleSignIns() {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(GOOGLE_PENDING)) continue;
      let at = 0;
      try { at = (JSON.parse(localStorage.getItem(k) || 'null') || {}).at || 0; } catch (e) { at = 0; }
      if (!(Date.now() - at < GOOGLE_MAX_AGE)) localStorage.removeItem(k);
    }
  } catch (e) { /* storage blocked */ }
}

async function startGoogleSignIn(returnPath) {
  const origin = googleStartOrigin(location.origin, config.appOrigin);
  if (origin !== location.origin) {
    // Google only sends people back to the app's own address: start from there (one more tap).
    location.assign(`${origin}/`);
    return;
  }
  dropOldGoogleSignIns();
  const state = randomString();
  const nonce = randomString();
  try {
    localStorage.setItem(GOOGLE_PENDING + state, JSON.stringify({ nonce, returnTo: safeReturnPath(returnPath), at: Date.now() }));
  } catch (e) {
    throw new AppError('This browser blocked the sign-in. Turn off private browsing for this site, or try another browser.', 'storage_blocked');
  }
  location.assign(googleAuthUrl({ clientId: config.googleClientId, origin, state, hashedNonce: await sha256Hex(nonce) }));
}

export const isGoogleReturn = () => typeof location !== 'undefined' && location.pathname === GOOGLE_RETURN_PATH;

// Finish a parent's Google sign-in that came back to /auth/google. Takes the token off the address straight
// away, and only uses it if it answers a sign-in this browser started (its state, in the last 15 minutes,
// each used once). Returns where to go next and, if it didn't work, what to tell the person.
export async function finishGoogleSignIn() {
  const got = parseGoogleReturn(location.hash);
  history.replaceState(null, '', GOOGLE_RETURN_PATH); // the token never stays on the address or in history
  let pending = null;
  if (/^[A-Za-z0-9_-]{20,100}$/.test(got.state)) {
    try {
      pending = JSON.parse(localStorage.getItem(GOOGLE_PENDING + got.state) || 'null');
      localStorage.removeItem(GOOGLE_PENDING + got.state);
    } catch (e) { pending = null; }
  }
  dropOldGoogleSignIns();
  const fresh = Boolean(pending && typeof pending.at === 'number' && Date.now() - pending.at < GOOGLE_MAX_AGE && pending.nonce);
  const next = safeReturnPath(fresh ? pending.returnTo : '/');
  if (got.error) {
    if (!fresh) return { next, error: null }; // not an answer to a sign-in started here: nothing to say
    const code = /^[a-z_]{3,40}$/.test(got.error) ? got.error : '';
    return { next, error: got.error === 'access_denied' ? 'Sign-in was cancelled. Try again when you’re ready.' : googleProblem(code ? ` (${code})` : '') };
  }
  if (!got.idToken || !fresh) {
    return { next, error: 'Google sign-in didn’t finish. Please tap “Parents: sign in with Google” again.' };
  }
  if (!auth) return { next, error: 'The app is not connected yet.' };
  const { error } = await auth.signInWithIdToken({ provider: 'google', token: got.idToken, nonce: pending.nonce });
  if (!error) return { next, error: null };
  const status = Number(error.status) || 0;
  if (error.name === 'AuthRetryableFetchError' && !status) return { next, error: 'You seem to be offline. Check your connection and try again.' };
  if (status >= 501) return { next, error: 'The sign-in service isn’t answering right now. Please try again in a few minutes.' };
  const code = error.code && /^[a-z_]{3,40}$/.test(error.code) ? error.code : status ? String(status) : '';
  return { next, error: googleProblem(code ? ` (${code})` : '') };
}

// Which sign-ins are switched on in Supabase (Google for parents; email for the Google Play review
// account). Asked once per page load.
let settingsCheck = null;
function authSettings() {
  if (!configured) return Promise.resolve(null);
  if (!settingsCheck) {
    settingsCheck = fetch(`${config.supabaseUrl}/auth/v1/settings`, { headers: { apikey: config.supabaseAnonKey }, signal: AbortSignal.timeout(8000) })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => { settingsCheck = null; return null; });
  }
  return settingsCheck;
}
export const googleSignInOn = () => authSettings().then((s) => Boolean(s && s.external && s.external.google));
export const reviewSignInOn = () => authSettings().then((s) => Boolean(s && s.external && s.external.email));

// The Google Play review account only: an email and password (every other password sign-in is refused).
export async function signInWithPassword(email, password) {
  if (!auth) throw new AppError('The app is not connected yet.', 'not_configured');
  try {
    sessionStorage.setItem(SIGNIN_PROVIDER, 'email');
    sessionStorage.removeItem(GRAPH_WANTED);
    sessionStorage.removeItem(GRAPH_KEY);
  } catch (e) { /* ignore */ }
  const { error } = await auth.signInWithPassword({ email: String(email || '').trim(), password: String(password || '') });
  if (!error) return;
  if (error.status === 400 || /invalid/i.test(error.message || '')) throw new AppError('That email and password didn’t work.', 'bad_password', 400);
  if (error.status === 429) throw new AppError('Too many tries. Wait a few minutes and try again.', 'rate_limited', 429);
  throw friendly(error);
}

// The sign-in the current session came from ('azure' or 'google').
export async function sessionProvider() {
  if (!auth) return null;
  const { data } = await auth.getSession();
  const s = data.session;
  return (s && s.user && s.user.app_metadata && s.user.app_metadata.provider) || null;
}

// Admins only: sign in again asking Microsoft for read access to OneDrive ('files'), or for that and
// changing group members ('groups'), then come back to returnTo.
export async function requestGraphAccess(returnTo, purpose = 'files') {
  if (!auth) throw new AppError('The app is not connected yet.', 'not_configured');
  sessionStorage.setItem(GRAPH_WANTED, JSON.stringify({ at: Date.now(), purpose }));
  try { sessionStorage.setItem(SIGNIN_PROVIDER, 'azure'); } catch (e) { /* ignore */ }
  const { error } = await auth.signInWithOAuth({
    provider: 'azure',
    options: { scopes: purpose === 'groups' ? GROUP_SCOPES : GRAPH_SCOPES, redirectTo: backTo(returnTo) },
  });
  if (error) throw friendly(error);
}

// The kept Microsoft token, if it allows what is needed ('files', or 'groups' for group changes) and the
// person who allowed it is still the one signed in.
export function graphToken(need = 'files') {
  try {
    const saved = JSON.parse(sessionStorage.getItem(GRAPH_KEY) || 'null');
    if (!saved || !saved.token) return null;
    if (saved.expires <= Date.now() || !saved.user || saved.user !== signedInUserId()) {
      forgetGraphToken();
      return null;
    }
    if (need === 'files' || saved.purpose === 'groups') return saved.token;
  } catch (e) { /* ignore */ }
  return null;
}

export function forgetGraphToken() {
  try { sessionStorage.removeItem(GRAPH_KEY); } catch (e) { /* ignore */ }
}

export async function signOut() {
  forgetGraphToken();
  try { sessionStorage.removeItem(GRAPH_WANTED); } catch (e) { /* ignore */ }
  if (auth) await auth.signOut();
}

// After a failed sign-in, Supabase/Microsoft put an error on the address. Anyone can make a link with any
// text there, so the app shows its own wording, plus the short error code for the academy to look up.
export function signInErrorFromUrl() {
  const params = new URLSearchParams(location.search);
  const hash = new URLSearchParams(location.hash.replace(/^#/, ''));
  const get = (k) => params.get(k) || hash.get(k) || '';
  const err = get('error');
  const code = get('error_code');
  const desc = get('error_description');
  if (!err && !code && !desc) return null;
  const aad = (desc.match(/\bAADSTS\d{5,6}\b/) || [])[0];
  const shown = [aad, /^[a-z_]{3,40}$/.test(code) ? code : '', /^[a-z_]{3,40}$/.test(err) ? err : '']
    .filter(Boolean).filter((x, i, all) => all.indexOf(x) === i).join(', ');
  const ref = shown ? ` (${shown})` : '';
  let started = 'azure';
  try { started = sessionStorage.getItem(SIGNIN_PROVIDER) || 'azure'; } catch (e) { /* ignore */ }
  if (err === 'access_denied' && !aad && (!code || code === 'provider_error')) return 'Sign-in was cancelled. Try again when you’re ready.';
  if (code === 'signup_disabled') return `New sign-ins are switched off at the moment. Please contact the academy${ref}.`;
  if (started === 'google') {
    return `Google sign-in didn’t finish. Google sign-in is for parents the academy has added; students and teachers sign in with Microsoft. If you’re a parent and it keeps happening, message the academy${ref}.`;
  }
  return `Microsoft sign-in didn’t finish. Please try again, and if it keeps happening, message the academy${ref}.`;
}

export function cleanAuthParams() {
  const url = new URL(location.href);
  let changed = false;
  if (url.searchParams.has('error') || /error/.test(url.hash)) {
    try { sessionStorage.removeItem(GRAPH_WANTED); } catch (e) { /* ignore */ }
  }
  for (const k of ['code', 'error', 'error_code', 'error_description', 'state']) {
    if (url.searchParams.has(k)) { url.searchParams.delete(k); changed = true; }
  }
  if (url.hash && /error|access_token/.test(url.hash)) { url.hash = ''; changed = true; }
  if (changed) history.replaceState(history.state, '', url.pathname + url.search + url.hash);
}

/* ---------- Photos for doubts and mock answers (Supabase Storage, private buckets) ---------- */
export const DOUBT_PHOTOS = 'doubt-photos';
export const ANSWER_PHOTOS = 'answer-photos';
const BUCKETS = [DOUBT_PHOTOS, ANSWER_PHOTOS];

function randomName() {
  const bytes = crypto.getRandomValues(new Uint8Array(12));
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

// Upload one (already compressed) JPEG into the signed-in person's own folder; returns its path.
export async function uploadPhoto(blob, bucket = DOUBT_PHOTOS) {
  if (!auth) throw new AppError('The app is not connected yet.', 'not_configured');
  if (!BUCKETS.includes(bucket)) throw new AppError('The photo didn’t upload. Please try again.', 'upload_failed');
  const { data } = await auth.getSession();
  const session = data.session;
  if (!session) throw new AppError('Please sign in again.', 'signed_out', 401);
  const path = `${session.user.id}/${randomName()}.jpg`;
  let res;
  try {
    res = await fetch(`${config.supabaseUrl}/storage/v1/object/${bucket}/${path}`, {
      method: 'POST',
      headers: {
        apikey: config.supabaseAnonKey,
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'image/jpeg',
        'x-upsert': 'false',
        'cache-control': 'max-age=3600',
      },
      body: blob,
      signal: AbortSignal.timeout(60000),
    });
  } catch (e) {
    throw new AppError('The photo didn’t upload. Check your connection and try again.', 'offline');
  }
  if (!res.ok) {
    const info = await res.json().catch(() => ({}));
    if (res.status === 413 || /size/i.test(info.message || '')) throw new AppError('That photo is too large. Try another one.', 'too_large');
    throw new AppError('The photo didn’t upload. Please try again.', 'upload_failed', res.status);
  }
  return path;
}

// Short-lived addresses to show photos; only photos the person may see come back.
export async function photoUrls(paths, bucket = DOUBT_PHOTOS) {
  const list = [...new Set((paths || []).filter(Boolean))];
  if (!auth || !list.length || !BUCKETS.includes(bucket)) return {};
  const { data } = await auth.getSession();
  if (!data.session) return {};
  const res = await fetch(`${config.supabaseUrl}/storage/v1/object/sign/${bucket}`, {
    method: 'POST',
    headers: { apikey: config.supabaseAnonKey, Authorization: `Bearer ${data.session.access_token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ expiresIn: 3600, paths: list }),
  }).catch(() => null);
  if (!res || !res.ok) return {};
  const out = {};
  for (const item of await res.json().catch(() => [])) {
    if (item && item.signedURL && !item.error) out[item.path] = `${config.supabaseUrl}/storage/v1${item.signedURL}`;
  }
  return out;
}
